import React, { useEffect, useState, useContext } from "react";
import { Link, useParams } from "react-router-dom";
import { buildAuthorPath, buildPublicationPath, buildPublisherPath } from "../../../utils/slugify";
import Header2 from "../../header2/index-two";
import InstructorSidebar from "../sidebar";
import { AppContext } from "../../../services/AuthContext";
import { getPublicationListByAuthoridsearch } from "../../../services/authorService";
import Spinner from "react-bootstrap/Spinner";

// --- HELPERS (Global yaklaşımla uyumlu) ---
const norm = (s) =>
  (s ?? "").toString().toLowerCase().normalize("NFKD").replace(/\s+/g, " ").trim();

const pickBlockItem = (blocks, names) => {
  if (!Array.isArray(blocks)) return null;
  const want = Array.isArray(names) ? names.map(norm) : [norm(names)];
  return blocks.find((it) => {
    const key = norm(it?.tag ?? it?.attributename);
    return want.includes(key);
  }) || null;
};

const pickLangFirst = (item, lang = "tr") => {
  const arr = item?.value ?? [];
  const row = arr.find((v) => norm(v?.lang) === norm(lang)) || arr[0];
  return Array.isArray(row?.value) ? row.value[0] ?? "" : "";
};

const extractTitle = (pub) => {
  const titleItem =
    pickBlockItem(pub?.templateInfo, ["başlık", "dergi adı", "sayı adı"]) ||
    pickBlockItem(pub?.publicationtypeInfo, ["başlık", "dergi adı", "sayı adı"]);
  return pickLangFirst(titleItem) || "-";
};

const extractType = (pub) => {
  if (pub?.publicationtype) return pub.publicationtype;
  const item =
    pickBlockItem(pub?.publicationtypeInfo, ["yayın türü"]) ||
    pickBlockItem(pub?.templateInfo, ["yayın türü"]);
  return pickLangFirst(item) || "";
};

const extractPubDate = (pub) => {
  const names = ["yayın tarihi"];
  const fromType = pickBlockItem(pub?.publicationtypeInfo, names);
  const fromTpl = pickBlockItem(pub?.templateInfo, names);
  const raw = pickLangFirst(fromType) || pickLangFirst(fromTpl) || "";
  const match = String(raw).match(/\d{4}/);
  return match ? match[0] : "";
};

// Üst Yayın bilgisini (Dergi/Kitap) parentList'ten çeken fonksiyon
const extractParentInfo = (pub) => {
  if (!Array.isArray(pub?.parentList) || pub.parentList.length === 0) return { name: "-", id: null };
  const rootParent = pub.parentList.find(p => {
    const t = extractType(p);
    return norm(t) === "dergi" || norm(t) === "kitap";
  }) || pub.parentList[pub.parentList.length - 1];
  return { name: extractTitle(rootParent), id: rootParent?.mysqlId || null };
};

const cleanLabel = (text) => {
  if (!text || text === "-") return text;
  if (text.toLowerCase().includes("kitap")) {
    return text
      .replace(/Akademik/gi, "")
      .replace(/Popüler/gi, "")
      .replace(/\s+/g, " ") // Kelime silindikten sonra kalan çift boşlukları teke indirir
      .trim();
  }
  return text;
};

const AuthorsPublication = () => {
  const { auth } = useContext(AppContext);
  const { id } = useParams();

  // --- STATE ---
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0); // 0-tabanlı
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  const fetchPublications = async () => {
    try {
      setLoading(true);
      // Servisin page ve size parametrelerini desteklediği varsayılmıştır
      const data = await getPublicationListByAuthoridsearch(id, 1, currentPage, pageSize, auth.token);
      if (data && data.status === "OK") {
        setTotalCount(data.count || 0);
        const parsedData = parsePublicationData(data.data);
        setPublications(parsedData);
      }
    } catch (error) {
      console.error("Error fetching publications:", error);
      setPublications([]);
    } finally {
      setLoading(false);
    }
  };

  const parsePublicationData = (data) => {
    if (!Array.isArray(data) || data.length === 0) return [];
    return data.map(({ elasticjson: pub }) => {
      const pubType = extractType(pub);
      const parent = extractParentInfo(pub);

      // PDF Link Mantığı
      const hasPdf = !!pub?.pdffile;
      const isOpenAccess = Number(pub?.openaccess) === 1;
      const viewerHref = hasPdf
        ? (isOpenAccess ? `/view-pdf-protectedaccess/${pub?.mysqlId}` : `/view-pdf/${pub?.mysqlId}`)
        : null;

      const goPdfTypes = ["Akademik Makale", "Akademik Kitap Bölümü", "Konferans ve Armağan Yazısı", "Konferans ve Armağan", "Popüler Dergi Yazısı"];
      const linkUrl = goPdfTypes.includes(pubType) && viewerHref ? viewerHref : buildPublicationPath({ title: extractTitle(pub), id: pub?.mysqlId });

      return {
        id: pub.mysqlId,
        title: extractTitle(pub),
        type: cleanLabel(pubType),
        date: extractPubDate(pub),
        linkUrl,
        parentTitle: cleanLabel(parent.name),
        parentMysqlId: parent.id
      };
    });
  };

  useEffect(() => {
    fetchPublications();
    window.scrollTo(0, 0);
  }, [currentPage, id]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="main-wrapper mt-5">
      <Header2 />
      <div className="page-content text-sm">
        <div className="container">
          <div className="row">
            <InstructorSidebar />
            <div className="col-xl-9 col-lg-9">
              <div className="settings-widget card-details">
                <div className="settings-menu p-0">
                  <div className="profile-heading d-flex justify-content-between align-items-center">
                    <h3>Yayınlar</h3>
                    <span className="text-muted small">Toplam: {totalCount} kayıt</span>
                  </div>
                  <div className="instructor-course-table">
                    <div className="table-responsive custom-table">
                      <table className="table mb-0">
                        <thead>
                          <tr>
                            <th>Başlık</th>
                            <th style={{ width: 150 }}>Tür</th>
                            <th>Üst Yayın</th>
                            <th style={{ width: 100 }}>Yayın Yılı</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="4" className="text-center py-5">
                                <Spinner animation="border" size="sm" variant="primary" />
                              </td>
                            </tr>
                          ) : publications.length > 0 ? (
                            publications.map((p) => (
                              <tr key={p.id}>
                                <td>
                                  <Link className="course-table-img" to={p.linkUrl}>
                                    {p.title}
                                  </Link>
                                </td>
                                <td>{p.type || ""}</td>
                                <td>
                                  {p.parentMysqlId ? (
                                    <Link to={buildPublicationPath({ title: p.parentTitle, id: p.parentMysqlId })}>
                                      {p.parentTitle}
                                    </Link>
                                  ) : (
                                    p.parentTitle
                                  )}
                                </td>
                                <td>{p.date || ""}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">Yayın bulunamadı.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination UI */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center pb-4 mt-3">
                      <nav aria-label="Page navigation">
                        <ul className="pagination pagination-sm mb-0 d-flex gap-2">
                          {/* İlk Sayfa */}
                          <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => setCurrentPage(0)}
                              disabled={currentPage === 0}
                            >
                              <i className="fas fa-angle-double-left"></i> İlk
                            </button>
                          </li>
                          {/* Önceki */}
                          <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => setCurrentPage(prev => prev - 1)}
                              disabled={currentPage === 0}
                            >
                              Önceki
                            </button>
                          </li>
                          {/* Sayfa Bilgisi */}
                          <li className="page-item">
                            <span className="btn btn-sm btn-primary disabled text-dark fw-bold">
                              {currentPage + 1} / {totalPages}
                            </span>
                          </li>
                          {/* Sonraki */}
                          <li className={`page-item ${currentPage + 1 >= totalPages ? "disabled" : ""}`}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => setCurrentPage(prev => prev + 1)}
                              disabled={currentPage + 1 >= totalPages}
                            >
                              Sonraki
                            </button>
                          </li>
                          {/* Son Sayfa */}
                          <li className={`page-item ${currentPage + 1 >= totalPages ? "disabled" : ""}`}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => setCurrentPage(totalPages - 1)}
                              disabled={currentPage + 1 >= totalPages}
                            >
                              Son <i className="fas fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorsPublication;